import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface OrganisationSelectProps {
  value: string
  onChange: (value: string) => void
  organisations: Array<{ id: number; name: string }>
  placeholder?: string
  isLoading?: boolean
  allowClear?: boolean
  disabled?: boolean
}

export function OrganisationSelect({
  value,
  onChange,
  organisations,
  placeholder = 'Select organisation…',
  isLoading = false,
  allowClear = false,
  disabled = false,
}: OrganisationSelectProps) {
  const [open, setOpen] = useState(false)

  const options = useMemo(
    () =>
      organisations.map((organisation) => ({
        id: organisation.id,
        name: organisation.name,
        value: String(organisation.id),
      })),
    [organisations],
  )

  const selectedOption = value ? options.find((option) => option.value === value) : undefined

  const handleSelect = (nextValue: string) => {
    onChange(nextValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
          disabled={disabled || isLoading}
        >
          {isLoading
            ? 'Loading…'
            : selectedOption
              ? selectedOption.name
              : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command className="[&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]]:py-2 [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-border [&_[cmdk-input]]:text-sm [&_[cmdk-input]]:placeholder:text-muted-foreground">
          <CommandInput
            placeholder="Search organisations…"
            className="text-sm"
          />
          <CommandList>
            <CommandEmpty>No organisation found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.name} ${option.id}`}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {option.name}
                </CommandItem>
              ))}
            </CommandGroup>
            {allowClear && value ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__clear__"
                    onSelect={() => handleSelect('')}
                    className="text-muted-foreground"
                  >
                    Clear selection
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
